/**
 * The way into "הנתונים שלך" from a screen that is otherwise a wall.
 *
 * It is the drop-in for `SubscriptionGate` and `IapPaywall`, and it replaces the
 * retired doctor's-summary link. The requirement it satisfies is unchanged: the
 * member's own history must be reachable FROM THE GATED STATE ITSELF, because
 * the member who meets the paywall is exactly the member whose data the app has
 * spent this whole feature promising is safe. It is simultaneously the store
 * reviewer's proof point for the never-imply-data-loss rule — an expired demo
 * account taps this and reaches a working screen.
 *
 * IT IS NOT GATED, AND MUST NEVER BECOME SO. A link that led to the paywall it
 * sits on would be worse than no link at all.
 *
 * A CALLBACK AND NOT A `<Link to>`: this component owns the copy — the half that
 * needed the thinking — and still imports no router, so it renders under
 * `renderToStaticMarkup` in a suite with no DOM. The hosts supply `onOpen`, and
 * `PROGRESS_PATH` is the one string they all navigate to.
 */

import { cn } from '@/lib/utils';

/**
 * A STATEMENT OF FACT, WITH NO CONDITION ATTACHED. "הנתונים שלך שמורים" is what
 * she is here to check; adding "כל עוד המנוי פעיל" would turn the promise into a
 * threat on the one screen where she is already deciding whether to leave.
 */
const COPY = {
  link: 'הנתונים שלך שמורים — לצפייה בהם',
} as const;

export interface ProgressLinkProps {
  /** How the host opens `/progress`. No router import — see above. */
  onOpen: () => void;
  className?: string;
}

/**
 * NOT A BUTTON SHAPE. It used to be a bordered pill, stacked directly under the
 * gate's filled "הצטרפי למנוי" — same size, same silhouette, so the screen
 * offered a member deciding whether to leave two things that looked equally like
 * the decision. This is not an offer; it is reassurance with a way to check it.
 * Text-styled in the brand colour keeps it unmistakably tappable (a store
 * reviewer has to find it) while letting the one real CTA stand alone, and the
 * 44px minimum is on the tap area rather than on a border.
 *
 * THE UNDERLINE IS NOT DECORATION. Dropping the border removed the only
 * non-colour signal that this is actionable, which is what SC 1.4.1 asks for
 * when a control is distinguished from surrounding text — and a member who
 * cannot separate the hue from the body copy is exactly the member most likely
 * to be looking at a paywall wondering whether her data survived.
 */
export const ProgressLink = ({ onOpen, className }: ProgressLinkProps) => (
  <button
    data-progress-link="true"
    type="button"
    onClick={onOpen}
    dir="rtl"
    className={cn(
      // `--primary-strong`, not `--primary`. Brand orange as TEXT measures 2.53:1
      // on this ground — a straight SC 1.4.3 failure on the one link a store
      // reviewer has to find and a lapsed member has to notice. Same hue, 38%
      // lightness, 5.7:1.
      'inline-flex min-h-[44px] items-center justify-center px-4 text-base font-medium',
      'text-[hsl(var(--primary-strong))] underline underline-offset-4',
      'transition-transform duration-150 active:scale-[0.97]',
      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-[hsl(var(--primary-strong))] focus-visible:outline-offset-2',
      className
    )}
  >
    {COPY.link}
  </button>
);

